/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useState } from "react"
import { useSelector } from "react-redux"
import { useHistory } from "react-router"
import { AppState } from "store/store.type"
import { DatePicker, Select } from "@duxton-web/ui"
import { MerchantStoreInfo } from "types/models/menu"
import dayjs, { Dayjs } from "dayjs"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { CustomSelect } from "components/common"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { SEARCH_LIMIT } from "components/search/searchBarConstants"
import { EventCategory, EventAction } from "constants/tracking"
import { TroyWebTracker } from "utils/tracking"
import {
  onSelectOption,
  onDeselectOption,
  useResolutionTypeMap,
  formatDateForDisplay,
} from "./utils"
import styles from "./incidentStyles.modules.css"
import { optionValue } from "../consts"
import { FeedbackStoreFilter } from "../FeedbackFilters/hooks/useFeedbackStoreList"

const { RangePicker } = DatePicker

interface IncidentListFilterProps {
  storeList: MerchantStoreInfo[] | undefined
  merchantID: string
  setMerchantID: (merchantID: string) => void
  setDateRange: (fromTime: string, endTime: string) => void
  selectedIncidentType: string[]
  setSelectedIncidentType: (selected: string[]) => void
  selectedResolutionType: string[]
  setSelectedResolutionType: (selected: string[]) => void
  isSingleStoreUser: boolean
  handleFilter: (obj: FeedbackStoreFilter) => void
  fromTime: string
  endTime: string
}

export const IncidentListFilter = ({
  storeList,
  merchantID,
  setMerchantID,
  setDateRange,
  selectedIncidentType,
  setSelectedIncidentType,
  selectedResolutionType,
  setSelectedResolutionType,
  isSingleStoreUser,
  handleFilter,
  fromTime,
  endTime,
}: IncidentListFilterProps) => {
  const history = useHistory()
  const {
    menu: { isFetchingOutletsSearch },
  } = useSelector((state: AppState) => state)
  const [searchError, setSearchError] = useState<string>("")
  const textMapIncidents = useTranslationsMapTyped({
    SevereFoodSafety: "incidents.incidentType.severeSafety",
    LightFoodSafety: "incidents.incidentType.lightSafety",
    OtherQuantityIssue: "incidents.incidentType.otherQuality",
    MissingOrWrongItem: "incidents.incidentType.missingWrong",
  })
  const textMapResolution = useResolutionTypeMap()
  const incidentTypeOptions = () => {
    const incidentTypes = Object.entries(textMapIncidents).map(
      ([value, label]) => {
        return {
          value,
          label,
        }
      },
    )
    return [
      {
        label: "All",
        value: "all",
      },
      ...incidentTypes,
    ]
  }
  const resolutionTypeOptions = () => {
    const resolutionTypes = Object.entries(textMapResolution).map(
      ([value, label]) => {
        return {
          value,
          label,
        }
      },
    )
    return [
      {
        label: "All",
        value: "all",
      },
      ...resolutionTypes,
    ]
  }
  const textMap = useTranslationsMapTyped({
    characterExceed: "common.characterExceed",
  })

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (!dates) {
      const defaultFromTime = dayjs()
        .subtract(7, "days")
        .startOf("day")
        .format(formatDateForDisplay)
      const defaultEndTime = dayjs()
        .endOf("day")
        .format(formatDateForDisplay)

      setDateRange(defaultFromTime, defaultEndTime)

      TroyWebTracker.sendEvent({
        category: EventCategory.incidentFilterByDate,
        action: EventAction.clickFilter,
        labels: {
          FROM_TIME: defaultFromTime,
          TO_TIME: defaultEndTime,
        },
      })
      return
    }

    const changedStartTime = dates[0]?.format(formatDateForDisplay) || ""
    const changedEndTime = dates[1]?.format(formatDateForDisplay) || ""

    setDateRange(changedStartTime, changedEndTime)

    TroyWebTracker.sendEvent({
      category: EventCategory.incidentFilterByDate,
      action: EventAction.clickFilter,
      labels: {
        FROM_TIME: changedStartTime,
        TO_TIME: changedEndTime,
      },
    })
  }

  const handleSearch = (val: string) => {
    if (val.length > SEARCH_LIMIT) {
      const errorMsg = textMap.characterExceed
        .replace("<current>", `${val.length}`)
        .replace("<limit>", `${SEARCH_LIMIT}`)
      setSearchError(errorMsg)
      return
    }

    setSearchError("")
    handleFilter({
      search: val,
    })
  }

  const onChangeStore = (value: string) => {
    setMerchantID(value)
    history.replace(`?merchantID=${value}`)

    TroyWebTracker.sendEvent({
      category: EventCategory.incidentFilterBySelectedMerchantID,
      action: EventAction.clickFilter,
      labels: {
        MERCHANT_ID: value,
      },
    })
  }

  return (
    <div className={styles.filterContainer}>
      <RangePicker
        allowEmpty
        // Removing Z from the time string to avoid timezone error
        value={[
          fromTime ? dayjs(fromTime.replace(/Z$/, "")) : null,
          endTime ? dayjs(endTime.replace(/Z$/, "")) : null,
        ]}
        onChange={onRangeChange}
        minDate={dayjs().subtract(90, "days")}
        maxDate={dayjs().endOf("day")}
      />
      <Select
        className={styles.fieldWidth}
        options={incidentTypeOptions()}
        placeholder="Incident type" // TODO: Update to content translation
        defaultValue={[optionValue.ALL]}
        mode="multiple"
        maxTagCount={2}
        onChange={value => {
          setSelectedIncidentType(value)
          TroyWebTracker.sendEvent({
            category: EventCategory.incidentFilterByIncidentType,
            action: EventAction.clickFilter,
            labels: {
              INCIDENT_TYPE: value,
            },
          })
        }}
        value={selectedIncidentType}
        allowClear
        onSelect={value =>
          onSelectOption(
            value as string,
            selectedIncidentType,
            setSelectedIncidentType,
            incidentTypeOptions(),
          )
        }
        onDeselect={value =>
          onDeselectOption(
            value as string,
            selectedIncidentType,
            setSelectedIncidentType,
            incidentTypeOptions(),
          )
        }
      />
      <Select
        className={styles.fieldWidth}
        options={resolutionTypeOptions()}
        placeholder="Resolution type" // TODO: Update to content translation
        defaultValue={[optionValue.ALL]}
        mode="multiple"
        maxTagCount={2}
        onChange={value => {
          setSelectedResolutionType(value)
          TroyWebTracker.sendEvent({
            category: EventCategory.incidentFilterByResolutionType,
            action: EventAction.clickFilter,
            labels: {
              RESOLUTION_TYPE: value.map(
                type =>
                  textMapResolution[
                    (type as unknown) as keyof typeof textMapResolution
                  ],
              ),
            },
          })
        }}
        value={selectedResolutionType}
        allowClear
        onSelect={value =>
          onSelectOption(
            value as string,
            selectedResolutionType,
            setSelectedResolutionType,
            resolutionTypeOptions(),
          )
        }
        onDeselect={value =>
          onDeselectOption(
            value as string,
            selectedResolutionType,
            setSelectedResolutionType,
            resolutionTypeOptions(),
          )
        }
      />
      {!isSingleStoreUser && (
        <CustomSelect
          className={styles.fieldWidth}
          data-testid="input-store"
          options={storeList?.map(opt => ({
            label: opt.merchantName,
            value: opt.merchantID,
          }))}
          searchable
          searchDelayMs={DEFAULT_DEBOUNCE_WAIT_TIME}
          loading={isFetchingOutletsSearch}
          onSearch={handleSearch}
          filterOption={false}
          customDropdownStyle={{ width: 400 }}
          onChange={onChangeStore}
          paginationProps={{ showPagination: false }}
          value={merchantID}
          error={searchError}
        />
      )}
    </div>
  )
}
